/**
 * Page-scoped form state for the brief editor.
 *
 * Owns title, values, and sections seeded ONCE from the fetched brief
 * (`useState(() => …)` initializer). The parent route keys the editor
 * surface on `brief.id`, so navigation to a different brief triggers a
 * full remount + re-seed — no effect-based synchronisation needed.
 *
 * Auto-save split:
 *   - `changeCounter` increments ONLY on `setTitle`/`setValue` mutations.
 *     `setSectionContent` updates state and flips `isDirty` (so the manual
 *     Save button enables) but DOES NOT bump the counter — `useBriefAutoSave`
 *     debounces on the counter, so section edits never trigger an auto-PATCH
 *     that would replace section UUIDs and destroy TipTap cursor state.
 *   - `getSubmitPayload()` returns `{ title, values }` only (auto-save).
 *   - `getFullSubmitPayload()` returns `{ title, values, sections }` mapped
 *     to the BE `UpdateBriefSectionDto` shape (no `id`); used by manual Save.
 *
 * `isValid` is derived during render as `title.trim().length > 0`. Both
 * payload accessors throw when invalid so a consumer that bypasses the gate
 * fails loudly rather than emitting a 400-bound PATCH.
 *
 * `isDirty` is derived during render via a shallow compare of the
 * current form state vs a baseline snapshot. `rebaseline(fresh)` replaces
 * the baseline (post-save success) so the form returns to a clean state
 * without an effect.
 *
 * NO React effect hook in this module. NO debounce here (debounce lives in
 * `useBriefAutoSave`).
 */
import { useCallback, useState } from 'react';

import type {
  BriefResponseDto,
  BriefSectionResponseDto,
} from '@inference.engine/workspace-api-client/web';

export interface UseBriefEditorFormParams {
  brief: BriefResponseDto;
}

interface BriefEditorFormSection {
  id: string;
  name: string;
  position: number;
  content: Record<string, unknown> | null;
  /** Typed field value; shape depends on the section's `type`. Null for prose sections. */
  value?: unknown;
}

interface BriefAutoSavePayload {
  title: string;
  values: Record<string, unknown>;
}

export interface BriefManualSavePayload extends BriefAutoSavePayload {
  sections: Array<{
    id: string;
    name: string;
    position: number;
    content: Record<string, unknown> | null;
    value?: unknown;
  }>;
}

export interface BriefEditorFormApi {
  title: string;
  values: Record<string, unknown>;
  sections: ReadonlyArray<BriefEditorFormSection>;
  isDirty: boolean;
  isValid: boolean;
  changeCounter: number;
  setTitle: (next: string) => void;
  /**
   * Adopt a title that was persisted OUTSIDE the editor (e.g. the rename
   * dialog's own `useUpdateBrief` PATCH). Updates BOTH the current value and
   * the baseline so: (1) the editor reflects the new title immediately, and
   * (2) the next auto-save does NOT re-PATCH the stale title and clobber the
   * rename. Deliberately does NOT bump `changeCounter` — there is nothing new
   * to save, so it must not trigger an auto-save.
   */
  syncTitle: (next: string) => void;
  setValue: (key: string, next: unknown) => void;
  setSectionContent: (sectionId: string, doc: Record<string, unknown>) => void;
  /**
   * Adopt section content that was persisted OUTSIDE the editor (an applied
   * agent suggestion PATCHes the brief directly). Updates BOTH the current
   * value and the baseline so: (1) the editor reflects the applied content,
   * and (2) the next manual Save does NOT ship the stale pre-apply content
   * and silently revert the suggestion. Does NOT bump `changeCounter` —
   * the content is already persisted, so it must not trigger an auto-save.
   */
  applySectionContent: (sectionId: string, doc: Record<string, unknown>) => void;
  /**
   * Byte-for-byte analogue of `setSectionContent` for a typed field's `value` instead of
   * prose `content`. R13 — marks dirty but does NOT bump `changeCounter`, so a field edit can
   * never enter the auto-save debounce window; only manual Save ships it.
   */
  setSectionValue: (sectionId: string, value: unknown) => void;
  /**
   * Byte-for-byte analogue of `applySectionContent` for `value` — adopts a value written
   * OUTSIDE the editor (an accepted AI suggestion). Updates current AND baseline so the next
   * manual Save does not revert it, and does NOT bump `changeCounter`.
   */
  applySectionValue: (sectionId: string, value: unknown) => void;
  /**
   * Bring this once-seeded form back in step with a brief a template reconcile has rewritten.
   * A section the form does not hold is adopted; one it already holds is reconciled field by
   * field against the server, which is the only party that knows what the refreshed template
   * accepts. Sections land in current AND baseline, so nothing the server did for free is
   * offered back as unsaved work.
   */
  adoptTemplateSections: (fresh: readonly BriefSectionResponseDto[]) => void;
  getSubmitPayload: () => Readonly<BriefAutoSavePayload>;
  getFullSubmitPayload: () => Readonly<BriefManualSavePayload>;
  /**
   * Update the baseline to reflect what was just successfully persisted.
   * Receives the EXACT payload that was submitted (NOT the server's
   * response) so any edits the user made WHILE the request was in flight
   * stay in the current state. The dirty check re-evaluates against the
   * new baseline, so a follow-up edit becomes the new dirty delta and
   * the next auto-save dispatches naturally.
   */
  rebaseline: (submitted: BriefManualSavePayload) => void;
}

interface FormState {
  title: string;
  values: Record<string, unknown>;
  sections: BriefEditorFormSection[];
  baseline: {
    title: string;
    values: Record<string, unknown>;
    sections: BriefEditorFormSection[];
  };
  changeCounter: number;
}

const toFormSection = (section: BriefSectionResponseDto): BriefEditorFormSection => ({
  id: section.id,
  name: section.name,
  position: section.position,
  content: section.content ?? null,
  value: section.value ?? null,
});

const seedFromBrief = (brief: BriefResponseDto): FormState => {
  const title = brief.title;
  const values: Record<string, unknown> = { ...(brief.values ?? {}) };
  const sections = brief.sections.map(toFormSection);
  return {
    title,
    values,
    sections,
    baseline: {
      title,
      values: { ...values },
      sections: sections.map((s) => ({ ...s })),
    },
    changeCounter: 0,
  };
};

const shallowEqualRecords = (a: Record<string, unknown>, b: Record<string, unknown>): boolean => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (a[key] !== b[key]) return false;
  }
  return true;
};

const contentEqual = (
  a: Record<string, unknown> | null,
  b: Record<string, unknown> | null,
): boolean => {
  if (a === b) return true;
  if (a === null || b === null) return false;
  // TipTap doc JSON — deep compare via stable serialization. The trees
  // are small (one section's content) and the comparison runs only on
  // dirty checks, so the cost is negligible. Reference equality alone
  // would miss baseline updates that happen via `rebaseline` (which
  // necessarily constructs fresh objects).
  return JSON.stringify(a) === JSON.stringify(b);
};

// Same reasoning as `contentEqual` — `value` is small per-section jsonb, and `rebaseline`
// necessarily constructs fresh objects, so reference equality alone would miss it.
const valueEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  return JSON.stringify(a) === JSON.stringify(b);
};

const sectionsEqual = (
  a: ReadonlyArray<BriefEditorFormSection>,
  b: ReadonlyArray<BriefEditorFormSection>,
): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i];
    const right = b[i];
    if (
      left.id !== right.id ||
      left.name !== right.name ||
      left.position !== right.position ||
      !contentEqual(left.content, right.content) ||
      !valueEqual(left.value, right.value)
    ) {
      return false;
    }
  }
  return true;
};

/**
 * A section the form already holds, reconciled against the server's copy of the same section.
 *
 * Name and position are the template's to state, so the server always wins them. `value` is the
 * briefer's, so an unsaved answer is kept - unless the server moved the stored one, which it does
 * only when the refreshed field no longer accepts it. An unsaved answer was written against the
 * config that was just replaced, so it fails the same parse the stored one failed, and keeping it
 * would trade one lost edit for a brief that cannot be saved at all.
 *
 * `content` is never taken from the server. The prose editor is not remounted by a reconcile, so
 * replacing content underneath it would put the form out of step with what is on screen, and
 * reconcile does not rewrite prose in any case. The baseline's content is left where it was, so
 * an unsaved sentence still reads as unsaved and an untouched section still reads as clean.
 */
const mergeServerSection = (
  held: BriefEditorFormSection,
  base: BriefEditorFormSection | undefined,
  server: BriefEditorFormSection,
): { section: BriefEditorFormSection; baseline: BriefEditorFormSection } => {
  const answerEdited = base !== undefined && !valueEqual(held.value ?? null, base.value ?? null);
  const serverMovedAnswer =
    base === undefined || !valueEqual(base.value ?? null, server.value ?? null);

  return {
    section: {
      ...server,
      content: held.content,
      value: answerEdited && !serverMovedAnswer ? held.value : server.value,
    },
    baseline: { ...server, content: base?.content ?? server.content },
  };
};

export const useBriefEditorForm = ({ brief }: UseBriefEditorFormParams): BriefEditorFormApi => {
  const [state, setState] = useState<FormState>(() => seedFromBrief(brief));

  const isValid = state.title.trim().length > 0;
  const isDirty =
    state.title !== state.baseline.title ||
    !shallowEqualRecords(state.values, state.baseline.values) ||
    !sectionsEqual(state.sections, state.baseline.sections);

  const setTitle = useCallback((next: string) => {
    setState((prev) => ({
      ...prev,
      title: next,
      changeCounter: prev.changeCounter + 1,
    }));
  }, []);

  const syncTitle = useCallback((next: string) => {
    setState((prev) => ({
      ...prev,
      title: next,
      baseline: { ...prev.baseline, title: next },
    }));
  }, []);

  const setValue = useCallback((key: string, next: unknown) => {
    setState((prev) => ({
      ...prev,
      values: { ...prev.values, [key]: next },
      changeCounter: prev.changeCounter + 1,
    }));
  }, []);

  const setSectionContent = useCallback((sectionId: string, doc: Record<string, unknown>) => {
    setState((prev) => ({
      ...prev,
      // Section edits update state and mark dirty, but DO NOT
      // increment `changeCounter`. Auto-save debounces on the counter,
      // so this keeps sections out of the auto-PATCH window.
      sections: prev.sections.map((s) => (s.id === sectionId ? { ...s, content: doc } : s)),
    }));
  }, []);

  const applySectionContent = useCallback((sectionId: string, doc: Record<string, unknown>) => {
    setState((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => (s.id === sectionId ? { ...s, content: doc } : s)),
      baseline: {
        ...prev.baseline,
        sections: prev.baseline.sections.map((s) =>
          s.id === sectionId ? { ...s, content: doc } : s,
        ),
      },
    }));
  }, []);

  const setSectionValue = useCallback((sectionId: string, value: unknown) => {
    setState((prev) => ({
      ...prev,
      // R13 — same rule as setSectionContent: dirty, but no changeCounter bump, so typed-field
      // edits stay out of the auto-PATCH window and ship only via manual Save.
      sections: prev.sections.map((s) => (s.id === sectionId ? { ...s, value } : s)),
    }));
  }, []);

  const applySectionValue = useCallback((sectionId: string, value: unknown) => {
    setState((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => (s.id === sectionId ? { ...s, value } : s)),
      baseline: {
        ...prev.baseline,
        sections: prev.baseline.sections.map((s) => (s.id === sectionId ? { ...s, value } : s)),
      },
    }));
  }, []);

  const adoptTemplateSections = useCallback((fresh: readonly BriefSectionResponseDto[]) => {
    setState((prev) => {
      const fromServer = new Map(fresh.map((s) => [s.id, toFormSection(s)]));
      const baselineById = new Map(prev.baseline.sections.map((s) => [s.id, s]));
      const heldIds = new Set(prev.sections.map((s) => s.id));

      const sections: BriefEditorFormSection[] = [];
      const baseline: BriefEditorFormSection[] = [];

      for (const section of prev.sections) {
        const server = fromServer.get(section.id);
        if (!server) {
          // A section the refetched brief does not report. Dropping it would take whatever is in
          // it with it, so it stays as the briefer last saw it.
          sections.push(section);
          baseline.push(baselineById.get(section.id) ?? section);
          continue;
        }
        const merged = mergeServerSection(section, baselineById.get(section.id), server);
        sections.push(merged.section);
        baseline.push(merged.baseline);
      }

      for (const section of fresh) {
        if (heldIds.has(section.id)) continue;
        const adopted = toFormSection(section);
        sections.push(adopted);
        baseline.push({ ...adopted });
      }

      if (
        sectionsEqual(sections, prev.sections) &&
        sectionsEqual(baseline, prev.baseline.sections)
      ) {
        return prev;
      }

      return { ...prev, sections, baseline: { ...prev.baseline, sections: baseline } };
    });
  }, []);

  const getSubmitPayload = useCallback((): Readonly<BriefAutoSavePayload> => {
    if (state.title.trim().length === 0) {
      throw new Error('useBriefEditorForm.getSubmitPayload: form is invalid (title is empty)');
    }
    return Object.freeze({
      title: state.title.trim(),
      values: { ...state.values },
    });
  }, [state.title, state.values]);

  const getFullSubmitPayload = useCallback((): Readonly<BriefManualSavePayload> => {
    if (state.title.trim().length === 0) {
      throw new Error('useBriefEditorForm.getFullSubmitPayload: form is invalid (title is empty)');
    }
    return Object.freeze({
      title: state.title.trim(),
      values: { ...state.values },
      sections: state.sections.map((s) => ({
        id: s.id,
        name: s.name,
        position: s.position,
        content: s.content,
        value: s.value ?? null,
      })),
    });
  }, [state.title, state.values, state.sections]);

  const rebaseline = useCallback((submitted: BriefManualSavePayload) => {
    // Non-destructive: keep `title`, `values`, `sections` (the user may
    // have typed more while the request was in flight). Only the
    // BASELINE moves forward so the dirty diff narrows to the new edits.
    // Section ids are preserved because we never touch `sections`
    // here, only the baseline mirror used for dirty comparison.
    setState((prev) => ({
      ...prev,
      baseline: {
        title: submitted.title,
        values: { ...submitted.values },
        sections: submitted.sections.map((s, index) => ({
          id: prev.sections[index]?.id ?? `s-${index}`,
          name: s.name,
          position: s.position,
          content: s.content ?? null,
          value: s.value ?? null,
        })),
      },
    }));
  }, []);

  return {
    title: state.title,
    values: state.values,
    sections: state.sections,
    isDirty,
    isValid,
    changeCounter: state.changeCounter,
    setTitle,
    syncTitle,
    setValue,
    setSectionContent,
    applySectionContent,
    setSectionValue,
    applySectionValue,
    adoptTemplateSections,
    getSubmitPayload,
    getFullSubmitPayload,
    rebaseline,
  };
};
